import { type Residence, residenceUpdateLocation } from '@repo/sdk/gen'
import { Button } from '@repo/ui/button'
import { DetailSection } from '@repo/ui/components/detail-section'
import { type PickedPlace, PlaceSearch } from '@repo/ui/components/place-search'
import { ResidenceMap } from '@repo/ui/components/residence-map'
import { Spinner } from '@repo/ui/spinner'
import { formatError } from '@repo/ui/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { MapPin } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'

const GOOGLE_MAPS_API_KEY: string | undefined = import.meta.env
	.VITE_GOOGLE_MAPS_API_KEY

type Site = Residence['sites'][number]

/**
 * Where a site of the residence is on the map, for the general admin and the facility
 * manager: search the address with Google Places, check the point on the map and save.
 * The position is part of the showcase, like the photos, so it is saved on its own in
 * every state of the call and never touches the data form.
 */
function LocationSection({
	residence,
	site
}: {
	residence: Residence
	site: Site
}) {
	const { t, i18n } = useTranslation()
	const queryClient = useQueryClient()
	const searchId = `place-search-${site.id}`
	// The place picked and not saved yet.
	const [picked, setPicked] = useState<PickedPlace | null>(null)

	const save = useMutation({
		mutationFn: (location: Site['location']) =>
			residenceUpdateLocation({
				body: { data: { id: residence.id, siteId: site.id, location } }
			}),
		onSuccess: async (_, location) => {
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.residences.all()
			})
			setPicked(null)
			toast.success(
				location
					? t('admin-residences:location.saved')
					: t('admin-residences:location.removed')
			)
		},
		onError: (error) => toast.error(formatError(error))
	})

	const point = picked
		? {
				latitude: picked.latitude,
				longitude: picked.longitude,
				address: picked.formattedAddress
			}
		: site.location && {
				latitude: site.location.latitude,
				longitude: site.location.longitude,
				address: `${site.address}, ${site.city}`
			}

	return (
		<DetailSection
			icon={MapPin}
			title={
				residence.sites.length > 1
					? t('admin-residences:location.title_site', {
							number: site.position,
							name: site.name
						})
					: t('admin-residences:location.title')
			}
		>
			<div className='flex max-w-3xl flex-col gap-5'>
				<p className='text-muted-foreground text-sm'>
					{t('admin-residences:location.description')}
				</p>
				{GOOGLE_MAPS_API_KEY ? (
					<>
						<div className='flex flex-col gap-2'>
							<label
								className='font-medium text-sm'
								htmlFor={searchId}
							>
								{t('admin-residences:location.search_label')}
							</label>
							<PlaceSearch
								apiKey={GOOGLE_MAPS_API_KEY}
								attribution={t(
									'admin-residences:hints.address_attribution'
								)}
								failedMessage={t(
									'admin-residences:location.search_failed'
								)}
								id={searchId}
								language={i18n.language}
								onPick={setPicked}
							/>
						</div>
						<ResidenceMap
							apiKey={GOOGLE_MAPS_API_KEY}
							label={t('admin-residences:location.map_label', {
								name: site.name
							})}
							language={i18n.language}
							points={
								point
									? [
											{
												id: site.id,
												name: site.name,
												...point
											}
										]
									: []
							}
						/>
					</>
				) : (
					<p className='text-muted-foreground text-sm'>
						{t('admin-residences:location.no_key')}
					</p>
				)}
				<PositionLine picked={picked} saved={site.location} />
				<div className='flex flex-wrap gap-3'>
					{picked && (
						<>
							<Button
								disabled={save.isPending}
								onClick={() =>
									save.mutate({
										latitude: picked.latitude,
										longitude: picked.longitude,
										googlePlaceId: picked.placeId
									})
								}
								type='button'
							>
								{save.isPending && <Spinner />}
								{t('admin-residences:location.save')}
							</Button>
							<Button
								disabled={save.isPending}
								onClick={() => setPicked(null)}
								type='button'
								variant='outline'
							>
								{t('admin-residences:location.discard')}
							</Button>
						</>
					)}
					{!picked && site.location && (
						<Button
							disabled={save.isPending}
							onClick={() => save.mutate(null)}
							type='button'
							variant='outline'
						>
							{save.isPending && <Spinner />}
							{t('admin-residences:location.remove')}
						</Button>
					)}
				</div>
			</div>
		</DetailSection>
	)
}

/** What is set now: the place picked and not saved yet, the saved position, or nothing. */
function PositionLine({
	picked,
	saved
}: {
	picked: PickedPlace | null
	saved: Site['location']
}) {
	const { t } = useTranslation()
	let text = t('admin-residences:location.none')
	if (picked) {
		text = t('admin-residences:location.pending', {
			address: picked.formattedAddress
		})
	} else if (saved) {
		text = t('admin-residences:location.current', {
			latitude: saved.latitude.toFixed(6),
			longitude: saved.longitude.toFixed(6)
		})
	}
	return (
		<p aria-live='polite' className='text-sm'>
			{text}
		</p>
	)
}

export { LocationSection }
